import { useEffect, useRef, useState } from "react";
import { loadPdfDocument } from "../../utils/pdfjsLoader";
import { waitForElement } from "../../utils/waitForElement";

const BLOCKED_SHORTCUT_KEYS = new Set(["s", "p"]);

const blockEvent = (event) => event.preventDefault();

const renderPages = async (pdf, container, scale, isCancelled) => {
  container.replaceChildren();
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    if (isCancelled()) return;
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.className = "mx-auto mb-4 max-w-full h-auto shadow-md bg-white";
    canvas.setAttribute("aria-label", `Page ${pageNumber}`);
    container.appendChild(canvas);
    const canvasContext = canvas.getContext("2d");
    if (!canvasContext) return;
    await page.render({ canvas, canvasContext, viewport }).promise;
  }
};

export default function WatermarkedPdfViewer({ data, scale = 1.3 }) {
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!data) return undefined;
    let cancelled = false;
    const loadingTask = loadPdfDocument(data);
    setStatus("loading");
    loadingTask.promise
      .then((pdf) =>
        waitForElement(() => containerRef.current, () => cancelled).then((container) =>
          renderPages(pdf, container, scale, () => cancelled)
        )
      )
      .then(() => !cancelled && setStatus("ready"))
      .catch(() => !cancelled && setStatus("error"));
    return () => {
      cancelled = true;
    };
  }, [data, scale]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && BLOCKED_SHORTCUT_KEYS.has(event.key?.toLowerCase())) {
        event.preventDefault();
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, []);

  return (
    <div
      className="relative select-none"
      onContextMenu={blockEvent}
      onDragStart={blockEvent}
      data-testid="watermarked-pdf-viewer"
    >
      {status === "loading" && (
        <p className="py-10 text-center text-sm text-gray-500">Loading report…</p>
      )}
      {status === "error" && (
        <p className="py-10 text-center text-sm text-red-600">
          The report preview could not be displayed.
        </p>
      )}
      <div ref={containerRef} />
    </div>
  );
}
